import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { effect, inject, Injectable, PLATFORM_ID, signal, WritableSignal } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Event,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

@Injectable({ providedIn: 'root' })
export class ScrollPositionService {
  private readonly router: Router = inject(Router);
  private readonly scroller: ViewportScroller = inject(ViewportScroller);
  private readonly platformId: object = inject(PLATFORM_ID);
  private readonly isBrowser: boolean = isPlatformBrowser(this.platformId);

  private positions: Map<string, [number, number]> = new Map();
  private currentUrl: WritableSignal<string> = signal('');
  private shouldRestore: boolean = false;

  private lastRouteComponent: any = null;

  constructor() {
    if (!this.isBrowser) return;

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        const [x, y] = this.scroller.getScrollPosition();
        this.positions.set(this.router.url, [x, y]);
        this.shouldRestore = false;
      }

      if (event instanceof NavigationEnd) {
        const snapshot: RouterStateSnapshot = this.router.routerState.snapshot;
        const currentComponent = this.extractDeepestComponent(snapshot.root);

        if (this.lastRouteComponent && currentComponent !== this.lastRouteComponent) {
          this.shouldRestore = true;
        }

        this.lastRouteComponent = currentComponent;
        this.currentUrl.set(this.router.url);
      }
    });

    effect(() => {
      const url = this.currentUrl();
      if (!url || !this.shouldRestore) return;

      this.shouldRestore = false;

      setTimeout(() => {
        const pos = this.positions.get(url) ?? [0, 0];
        this.scroller.scrollToPosition(pos);
      }, 0);
    });
  }

  private extractDeepestComponent(route: ActivatedRouteSnapshot): any {
    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.component;
  }
}
